<template>
    <div class="map-container point-of-sale-map" role="region" :aria-label="t('map_description')">
        <a href="#after-map" class="skip-link">{{ t('skip_map') }}</a>
        <div ref="mapContainer" class="map"></div>
        <div id="after-map" tabindex="-1"></div>
    </div>
</template>

<script setup>
import 'maplibre-gl/dist/maplibre-gl.css';

import { useI18n } from 'vue-i18n';
import { ref, watch, onMounted, nextTick } from 'vue';

import { useMap } from '@/composables/useMap';
import PointOfSaleItem from '@/points-of-sale/components/PointOfSaleItem';

import blueMapPinUrl from '@@/img/blue-map-pin.svg';
import redMapPinUrl from '@@/img/red-map-pin.svg';

const props = defineProps({
    pointsOfSale: {
        type: Array,
        default: () => [],
    },
    userPosition: {
        type: Array,
        default: () => [],
    },
    addressPosition: {
        type: Array,
        default: () => [],
    },
    pointPosition: {
        type: Array,
        default: () => [],
    },
    selectedPointIndex: {
        type: [Number, String],
        default: 1,
    },
    meta: {
        type: Object,
        default: null
    }
});

const posSource = 'pos-source';
const posLayer = 'pos-layer';

const pinStyles = {
    blue: { url: blueMapPinUrl, width: 30, height: 44, fontSize: 10 },
    red: { url: redMapPinUrl, width: 34, height: 50, fontSize: 13 },
};
// Vertical center of the pin's white circle, as a ratio of its height.
const pinCircleCenter = 13.86 / 41;
const pinFontFamily = 'Figtree, Helvetica, Roboto, Arial, sans-serif';
const pinImagePattern = /^pin-(blue|red)-(\d+)$/;

const pinSources = {};

const mapContainer = ref(null);
const {
    map,
    createMap,
    addMarker,
    setUserPosition,
    openPopup,
    closePopup,
    onFeatureClick,
    loadImage,
    addCanvasImage,
    setGeoJSONSource,
} = useMap({ popupOptions: { offset: 25 } });
let addressMarker = null;
let isLayerReady = false;
let popupPointIndex = null;

function initializeMap() {
    createMap(mapContainer.value, {
        center: [-71.207, 46.8],
        zoom: 11,
        minZoom: 10,
    });

    map.value.on('load', async () => {
        renderUserPosition();
        renderAddressPosition();
        recenterMap({ animate: false });

        try {
            await loadPinSources();
        } catch (error) {
            console.error('Failed to load the map pins:', error); // eslint-disable-line no-console
        }
        addPointsLayer();
    });

    map.value.on('styleimagemissing', onStyleImageMissing);
    onFeatureClick([posLayer], (feature) => {
        const point = props.pointsOfSale.find((p) => p.index === feature.properties.index);
        if (point) {
            popupPointIndex = point.index;
            openPopup([point.longitude, point.latitude], PointOfSaleItem, () => ({
                pointOfSale: point,
                isSelected: point.index === props.selectedPointIndex,
                messages: pointItemMessages,
                meta: props.meta,
            }));
        }
    });
};

const { t } = useI18n({
    messages: {
        fr: {
            accessible_point_of_sale: 'Point de vente accessible',
            itinerary: 'Itinéraire',
            itinerary_to: 'Itinéraire vers',
            business_hours: 'Heures d’ouverture',
            skip_map: 'Passer la carte',
            map_description: 'Carte interactive des points de vente',
        },
        en: {
            accessible_point_of_sale: 'Accessible point of sale',
            itinerary: 'Itinerary',
            itinerary_to: 'Itinerary to',
            business_hours: 'Business hours',
            skip_map: 'Skip map',
            map_description: 'Interactive map of points of sale',
        },
    },
});
const pointItemMessages = {
    accessible_point_of_sale: t('accessible_point_of_sale'),
    itinerary: t('itinerary'),
    itinerary_to: t('itinerary_to'),
    business_hours: t('business_hours'),
};

async function loadPinSources() {
    await Promise.all(Object.entries(pinStyles).map(async ([color, { url, fontSize }]) => {
        pinSources[color] = await loadImage(url);
        await document.fonts.load(`${fontSize}px ${pinFontFamily}`).catch(() => {});
    }));
}

function onStyleImageMissing({ id }) {
    const [, color, index] = id.match(pinImagePattern) ?? [];
    if (!color || !pinSources[color]) return;

    const { width, height, fontSize } = pinStyles[color];
    addCanvasImage(id, width, height, (context) => {
        context.drawImage(pinSources[color], 0, 0, width, height);
        context.font = `${fontSize}px ${pinFontFamily}`;
        context.letterSpacing = '-0.5px';
        context.fillStyle = '#000';
        context.textAlign = 'center';
        context.textBaseline = 'middle';
        context.fillText(index, width / 2, height * pinCircleCenter);
    });
}

function getPointsGeoJSON() {
    return {
        type: 'FeatureCollection',
        features: props.pointsOfSale.map((point) => ({
            type: 'Feature',
            geometry: {
                type: 'Point',
                coordinates: [point.longitude, point.latitude],
            },
            properties: {
                index: point.index,
                selected: point.index === props.selectedPointIndex,
            },
        })),
    };
}

function addPointsLayer() {
    setGeoJSONSource(posSource, getPointsGeoJSON());

    map.value.addLayer({
        id: posLayer,
        type: 'symbol',
        source: posSource,
        layout: {
            'icon-image': ['concat', 'pin-', ['case', ['get', 'selected'], 'red', 'blue'], '-', ['to-string', ['get', 'index']]],
            'icon-allow-overlap': true,
            'icon-ignore-placement': true,
            'symbol-sort-key': ['case', ['get', 'selected'], 1, 0],
        },
    });

    isLayerReady = true;
}

function renderPoints() {
    if (!isLayerReady) return;

    setGeoJSONSource(posSource, getPointsGeoJSON());
}

function renderUserPosition() {
    if (!map.value) return;

    const [lat, lng] = props.userPosition;
    setUserPosition(props.userPosition.length ? [lng, lat] : null);
}

function renderAddressPosition() {
    if (!map.value) return;

    addressMarker?.remove();
    if (!props.addressPosition.length) return;

    const [lat, lng] = props.addressPosition;
    const el = document.createElement('div');
    el.className = 'address-marker';
    addressMarker = addMarker(el, [lng, lat]);
}

watch(
    () => props.pointPosition,
    () => scheduleRecenter(),
);

watch(
    () => props.selectedPointIndex,
    (newIndex) => {
        if (popupPointIndex !== newIndex) closePopup();
        renderPoints();
    },
);

watch(
    () => props.pointsOfSale,
    () => {
        closePopup();
        renderPoints();
        scheduleRecenter();
    },
);

watch(
    () => props.addressPosition,
    () => {
        renderAddressPosition();
        scheduleRecenter();
    },
);

watch(
    () => props.userPosition,
    () => {
        renderUserPosition();
        scheduleRecenter();
    },
);

let isRecenterScheduled = false;
function scheduleRecenter() {
    if (isRecenterScheduled) return;

    isRecenterScheduled = true;
    nextTick(() => {
        isRecenterScheduled = false;
        recenterMap();
    });
}

function recenterMap({ animate = true } = {}) {
    let position = props.pointPosition;
    if (props.userPosition.length) position = props.userPosition;
    if (props.addressPosition.length) position = props.addressPosition;
    if (!position.length || !map.value) return;

    const camera = { center: [position[1], position[0]], zoom: 14 };
    if (animate) {
        map.value.easeTo(camera);
    } else {
        map.value.jumpTo(camera);
    }
}

onMounted(initializeMap);
</script>

<style lang="scss">
@import "vue/map";

.point-of-sale-map {
    .maplibregl-popup {
        width: 300px;
        max-width: none !important;
        @include breakpoint(500px) {
            width: 345px;
        }
        .point-container {
            padding: 16px 10px 70px;
        }
        .point-button {
            gap: 16px;
        }
        .point-buttons-container {
            left: 46px;
        }
        .itinerary-form {
            .button {
                @include rem-typo(12px);
            }
        }
    }
}
.maplibregl-popup-content {
    padding: 0;
    .map-pin {
        &.is-red {
            .map-pin-index {
                top: 10px;
            }
        }
        &.is-blue  {
            .map-pin-index {
                top: 8px;
            }
        }
    }
}
.address-marker {
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background-color: $color-blue-60;
}
</style>

<style lang="scss" scoped>
.map-container {
    position: relative;
    margin-top: 16px;
    border-radius: 15px;
    overflow: hidden;
    @include breakpoint(large) {
        border-radius: 0;
        margin: 0;
    }
}
.map {
    height: 100%;
}
</style>
